// Keyless map/geo helpers for the Notrufplan (emergency plan), based purely on
// OpenStreetMap services — no API key required:
//   - Nominatim  : address -> coordinates (geocoding)
//   - Overpass   : EINE Umkreisabfrage fuer Krankenhaus / D-Arzt / Augenarzt /
//                  Apotheke; Zuordnung und Eignung entscheidet notfallStellen.ts
//   - staticmap.openstreetmap.de : static map image with coloured markers
// Phone numbers come from OSM tags where available; gaps are filled separately
// (optionally via AI) and must always be verified — this is a safety document.
//
// Usage policy: Nominatim/Overpass are community servers — light, occasional use
// only (~1 request/second). Attribution "© OpenStreetMap-Mitwirkende" is required
// wherever the map/data is shown.

export type { LatLng } from './notfallStellen';
import {
  KandidatenNachKategorie, KATEGORIEN, LatLng, NotfallKategorie,
  bauNamensAbfrage, bauOverpassAbfrage, besterVorschlag, entfernungMeter, leseOverpass,
  pruefeOverpassAntwort, sortiereKandidaten, zuKandidaten,
} from './notfallStellen';

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

// Globale Serialisierung aller Overpass-Anfragen (~1 req/s laut Policy) — parallele
// Bursts führen sonst zu 429 (Too Many Requests). Jeder Call wartet auf den vorigen.
// Seit die Umkreissuche mit EINER Abfrage auskommt (siehe `sucheStellen`) kostet
// das im Regelfall nichts mehr: es gibt keinen zweiten Aufruf, auf den zu warten
// wäre. Die Kette bleibt trotzdem stehen — zwei Nutzer im selben Browser-Tab,
// Wizard und Editor hintereinander, ein zweiter Klick.
let overpassKette: Promise<unknown> = Promise.resolve();
function throttle<T>(fn: () => Promise<T>): Promise<T> {
  const lauf = overpassKette.then(fn, fn);
  overpassKette = lauf.then(() => sleep(1100), () => sleep(1100));
  return lauf;
}

// Eine Overpass-Query über unseren osm-proxy, um CORS und blockierte IPs zu umgehen.
//
// Der Statuscode allein reicht NICHT: bei einem Zeitablauf antwortet Overpass
// mit 200, leerer Trefferliste und einem `remark`-Feld. Ohne `pruefeOverpassAntwort`
// sieht das aus wie „es gibt hier kein Krankenhaus" — genau daran ist die Suche
// nach der Zusammenfassung auf eine Abfrage gescheitert.
async function overpassFetch(q: string): Promise<any> {
  const url = `/api/osm-proxy?service=overpass`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: q }),
  });
  if (!res.ok) {
    throw new Error(`Umkreissuche fehlgeschlagen (${res.status}).`);
  }
  const data = await res.json();
  pruefeOverpassAntwort(data);
  return data;
}

/** Haversine distance in metres. */
export const distanz = entfernungMeter;

/** Address -> coordinates via osm-proxy. */
export async function geocode(adresse: string): Promise<LatLng> {
  const url = `/api/osm-proxy?service=geocode&q=${encodeURIComponent(adresse)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Adresssuche fehlgeschlagen (${res.status}).`);
  const data = await res.json();
  if (!Array.isArray(data) || !data[0]) throw new Error('Adresse nicht gefunden.');
  return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
}

/**
 * Umkreissuche in zwei Stufen — die billige immer, die teure nur bei Bedarf.
 *
 * Stufe 1 beantwortet alle gesuchten Kategorien in EINER Abfrage über
 * Bounding-Boxen und Fachgebiets-Tags (gemessen ~1,7 s für alle vier).
 * Stufe 2 sucht Augenarzt und Durchgangsarzt zusätzlich über den Namen — die
 * kostet ein Vielfaches und läuft nur, wenn Stufe 1 die Kategorie nicht
 * beantwortet hat.
 *
 * Scheitert Stufe 2, ist das NICHT fatal: die Treffer aus Stufe 1 bleiben
 * stehen, und die Kategorie zählt eben als „nicht gefunden". Scheitert Stufe 1,
 * wirft sie — dann gibt es wirklich nichts zu zeigen, und der Aufrufer
 * entscheidet, was noch aus der geprüften Datenbank steht.
 *
 * `gesperrt` sind Schlüssel, die Mitarbeiter verworfen haben — sie werden
 * schon hier aussortiert und erreichen die Oberfläche gar nicht erst.
 */
export async function sucheStellen(
  center: LatLng,
  kategorien: NotfallKategorie[],
  gesperrt: Set<string> = new Set(),
): Promise<KandidatenNachKategorie> {
  const grund = await throttle(() => overpassFetch(bauOverpassAbfrage(center, kategorien)));
  const treffer = zuKandidaten(center, leseOverpass(grund), kategorien, gesperrt);

  // Nur die Arztkategorien kennen einen Namens-Nachschlag, und nur, solange
  // die Grundabfrage sie nicht schon brauchbar beantwortet hat.
  const offen = (['augenarzt', 'durchgangsarzt'] as NotfallKategorie[])
    .filter(k => kategorien.includes(k) && !besterVorschlag(treffer[k]));
  if (!offen.length) return treffer;

  const abfrage = bauNamensAbfrage(center, offen);
  if (!abfrage) return treffer;
  try {
    const nachschlag = await throttle(() => overpassFetch(abfrage));
    const weitere = zuKandidaten(center, leseOverpass(nachschlag), offen, gesperrt);
    for (const k of KATEGORIEN) {
      if (!weitere[k].length) continue;
      treffer[k] = sortiereKandidaten([...treffer[k], ...weitere[k]]);
    }
  } catch (e) {
    console.warn('Namens-Nachschlag fehlgeschlagen — es bleibt bei den Treffern der Grundabfrage.', e);
  }
  return treffer;
}

// ── Static map built client-side from OSM tiles ─────────────────────────────
// staticmap.openstreetmap.de was discontinued (DNS gone); instead we stitch the
// standard OSM tiles (which send CORS headers) onto a canvas and draw the markers
// ourselves. Fully keyless and proxy-free. tile.openstreetmap.org usage policy:
// light/occasional use only, attribution required (drawn onto the image).

const KARTE_W = 640;
const KARTE_H = 384;
const TILE = 256;
// Markerfarben je KATEGORIE, nicht je Arrayposition. Vorher lief die Farbe
// ueber den Index (`MARKER_HEX[i]`): fiel eine Kategorie aus der Suche heraus,
// rutschten alle folgenden Marker eine Farbe weiter und die Legende im PDF
// behauptete etwas Falsches — auf einem Notrufplan.
export const MARKER_FARBE = {
  projekt: '#d11111',
  krankenhaus: '#1769d1',
  durchgangsarzt: '#7b3fbf',
  augenarzt: '#2a8c46',
  apotheke: '#e0a500',
} as const;

/** Ein Kartenpunkt traegt seine eigene Farbe und sein Kuerzel. */
export interface KartenPunkt extends LatLng {
  farbe?: string;
  kuerzel?: string;
}

const lngToFx = (lng: number, z: number) => (lng + 180) / 360 * Math.pow(2, z);
const latToFy = (lat: number, z: number) => {
  const r = lat * Math.PI / 180;
  return (1 - Math.asinh(Math.tan(r)) / Math.PI) / 2 * Math.pow(2, z);
};

function ladeTile(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Tile konnte nicht geladen werden: ${src}`));
    img.src = src;
  });
}

/** Highest zoom (<= 16) at which all points fit into the viewport with padding. */
function passendesZoom(punkte: LatLng[]): number {
  const pad = 60;
  for (let z = 16; z >= 9; z--) {
    const xs = punkte.map(p => lngToFx(p.lng, z) * TILE);
    const ys = punkte.map(p => latToFy(p.lat, z) * TILE);
    const breite = Math.max(...xs) - Math.min(...xs);
    const hoehe = Math.max(...ys) - Math.min(...ys);
    if (breite <= KARTE_W - 2 * pad && hoehe <= KARTE_H - 2 * pad) return z;
  }
  return 9;
}

/** Auto-fit zoom for a set of points (project + stops). */
export function autoZoom(center: LatLng, stellen: LatLng[]): number {
  return passendesZoom([center, ...stellen]);
}

/**
 * Builds a static map (PNG data URL) from OSM tiles with coloured markers.
 * Jeder Punkt bringt Farbe und Kuerzel selbst mit (`MARKER_FARBE`); ohne Angabe
 * bleibt es beim Projektrot fuer den ersten und Blau fuer die uebrigen. Pass an
 * explicit `zoom` for the editor +/- control; otherwise it auto-fits all points.
 * Throws on canvas failure so the caller can fall back to a list-only Notrufplan.
 */
export async function buildStaticMap(center: KartenPunkt, stellen: KartenPunkt[], zoom?: number): Promise<string> {
  const punkte: KartenPunkt[] = [{ ...center, farbe: center.farbe || MARKER_FARBE.projekt, kuerzel: center.kuerzel || 'P' }, ...stellen];
  const z = Math.max(3, Math.min(19, zoom ?? passendesZoom(punkte)));
  const n = Math.pow(2, z);

  // Centre the viewport on the bounding-box centre so every marker is visible.
  const fxs = punkte.map(p => lngToFx(p.lng, z));
  const fys = punkte.map(p => latToFy(p.lat, z));
  const cx = ((Math.min(...fxs) + Math.max(...fxs)) / 2) * TILE;
  const cy = ((Math.min(...fys) + Math.max(...fys)) / 2) * TILE;
  const originX = cx - KARTE_W / 2;
  const originY = cy - KARTE_H / 2;

  const canvas = document.createElement('canvas');
  canvas.width = KARTE_W;
  canvas.height = KARTE_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas nicht verfügbar.');
  ctx.fillStyle = '#e8e8e8';
  ctx.fillRect(0, 0, KARTE_W, KARTE_H);

  // Draw every tile covering the viewport.
  const tileX0 = Math.floor(originX / TILE);
  const tileX1 = Math.floor((originX + KARTE_W) / TILE);
  const tileY0 = Math.max(0, Math.floor(originY / TILE));
  const tileY1 = Math.min(n - 1, Math.floor((originY + KARTE_H) / TILE));

  const jobs: Promise<void>[] = [];
  for (let tx = tileX0; tx <= tileX1; tx++) {
    for (let ty = tileY0; ty <= tileY1; ty++) {
      const wrappedX = ((tx % n) + n) % n;
      const url = `https://tile.openstreetmap.org/${z}/${wrappedX}/${ty}.png`;
      const dx = tx * TILE - originX;
      const dy = ty * TILE - originY;
      jobs.push(ladeTile(url).then(img => { ctx.drawImage(img, dx, dy); }).catch(() => { /* fehlendes Tile = grau lassen */ }));
    }
  }
  await Promise.all(jobs);

  // Draw markers as coloured dots with a white ring and the category letter.
  // Der Buchstabe ist der Ausweg fuer den Schwarzweissdruck und fuer zwei
  // Marker, die dicht beieinander liegen — Farbe allein reicht dort nicht.
  punkte.forEach((p) => {
    const mx = lngToFx(p.lng, z) * TILE - originX;
    const my = latToFy(p.lat, z) * TILE - originY;
    ctx.beginPath();
    ctx.arc(mx, my, 9, 0, Math.PI * 2);
    ctx.fillStyle = p.farbe || MARKER_FARBE.krankenhaus;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    if (p.kuerzel) {
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(p.kuerzel, mx, my + 0.5);
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    }
  });

  // Attribution (required by the OSM tile usage policy).
  ctx.font = '11px sans-serif';
  const hinweis = '© OpenStreetMap-Mitwirkende';
  const tw = ctx.measureText(hinweis).width;
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.fillRect(KARTE_W - tw - 8, KARTE_H - 16, tw + 8, 16);
  ctx.fillStyle = '#333';
  ctx.fillText(hinweis, KARTE_W - tw - 4, KARTE_H - 4);

  return canvas.toDataURL('image/png');
}
